import { z } from 'zod';
import { ScopeTypeSchema } from './billing.js';
import { SlugSchema } from './slug.js';
// What we sell, and what a subject is on.
//
// A plan is a row core-api stores and the admin console edits — not code in an
// app. It holds only quantities of the socle (credits, a count of sandboxes, a
// unitless coefficient); the euro, the payment and the euro→credit conversion
// stay app-side. See `docs/strategy-consommation-et-quotas.md`.
/**
 * How long a period lasts, in months.
 *
 * `periodCredits` states an amount; this states what it is an amount *per*.
 * One constant rather than one assumption per caller — the proration and,
 * later, the renewal both read it, and neither gets to disagree about what a
 * month-long period is.
 *
 * Not a field on the plan: a yearly offer is a commitment of twelve periods
 * (`termPeriods`), its credits still granted month by month.
 */
export const PERIOD_MONTHS = 1;
/**
 * Everything that distinguishes one plan from another.
 *
 * Credit figures are in WHOLE credits, the unit a plan is written in and the
 * one an admin types. core-api stores integers in a fine sub-unit; the
 * conversion (`creditsToStored`) happens where a lot is written, never here.
 *
 * `coefficient` is not a limit, but it belongs with them: it is one of the
 * things a plan decides, and splitting it off is how a plan ends up granting
 * one offer's credits at another's price.
 */
export const PlanLimitsSchema = z.object({
    // Granted at each renewal, use-it-or-lose-it.
    periodCredits: z.number().int().nonnegative(),
    // Concurrently active sandboxes. Absent = unlimited; 0 = none.
    maxActiveSandboxes: z.number().int().nonnegative().optional(),
    // Multiplies the rate card at debit time. The card prices what an offering
    // costs; this is the commercial position on top of it, which is why it is not
    // baked into the published catalogue — a new plan must not force one to be
    // regenerated. Recorded on every movement it prices.
    coefficient: z.number().positive(),
    // How far below zero the balance may go before calls are refused. A credit
    // line, deliberately granted and owed — not credits, and not the same thing
    // as a recharge, which never goes below zero.
    //
    // A property of the plan alone: a client who needs another one gets a plan of
    // their own, rather than a plan bent for them one field at a time.
    overrunAllowance: z.number().int().nonnegative()
});
export const PlanSchema = z.object({
    id: z.string(),
    // A stable name an app's code refers to the plan by — its waiting plan, its
    // trial — where an id generated in a database cannot be written. Optional:
    // only the plans some code names need one. Unique, and editable: renaming it
    // breaks the code that refers to it, which is the renamer's to know.
    slug: z.string().optional(),
    // What the plan is called. Empty for a plan written before plans had a
    // name, until an operator gives it one.
    name: z.string(),
    limits: PlanLimitsSchema,
    // Whether the end customer pays for it. Leaving a plan nobody pays for for
    // one that is paid starts the customer's cycle afresh — that is when their
    // paying starts.
    paid: z.boolean(),
    // How many periods a commitment lasts. 1 is month to month.
    termPeriods: z.number().int().positive(),
    // Whether a commitment starts again when it ends. Without, the subscription
    // ends with its first commitment — a trial, a fixed-length contract.
    autoRenew: z.boolean(),
    // Whether it is still offered. A plan switched off is offered to nobody; the
    // console can still put someone on it, and its subscribers stay on it.
    active: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string()
});
/**
 * A subject on a plan, over a stretch of time.
 *
 * A history, not a pointer: "since when is this client on this plan" and "what
 * were they on last quarter" are questions an operator is asked, and a
 * current-state column answers neither.
 *
 * Keyed on the scope like credit lots, so an organisation's contract is a
 * subscription of scope `organization` without a schema change.
 *
 * It says which plan and for how long, and nothing else: what a subject gets is
 * the plan's business. A client who needs different terms gets a plan of their
 * own.
 */
export const SubscriptionSchema = z.object({
    id: z.string(),
    scopeType: ScopeTypeSchema,
    scopeId: z.string(),
    planId: z.string(),
    startsAt: z.string(),
    // The instant periods are counted from: each one starts a whole number of
    // months after it. Fixed when a subject first goes on a plan, and carried
    // over unchanged by every change that follows without a gap — the renewal
    // dates are computed from it, never read off an envelope.
    anchorAt: z.string(),
    // Where the commitment in course started: a renewal date. Commitments follow
    // one another from it, `termPeriods` periods at a time.
    termStartsAt: z.string(),
    // Absent = until something ends it. Written when a change replaces this
    // subscription, from the start on a plan that does not renew, and by a
    // cancellation.
    endsAt: z.string().optional(),
    // When it was cancelled: it then ends with its commitment.
    cancelledAt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string()
});
// --- admin wire shapes ---
export const CreatePlanRequestSchema = z.object({
    slug: SlugSchema.optional(),
    name: z.string().trim().min(1),
    limits: PlanLimitsSchema,
    // Left out: paid, month to month, renewing, offered — a plan as it was before
    // it had a life cycle.
    paid: z.boolean().optional(),
    termPeriods: z.number().int().positive().optional(),
    autoRenew: z.boolean().optional(),
    active: z.boolean().optional()
});
// Field omitted means "leave it alone"; a `null` slug clears it.
export const UpdatePlanRequestSchema = CreatePlanRequestSchema.partial().extend({
    slug: SlugSchema.nullable().optional()
});
/**
 * Put a subject on a plan, from a given instant.
 *
 * The same operation creates a subscription and changes one — signing up is
 * only the case where the defaults do.
 *
 * `effectiveAt` may be in the future (the change is written once and the
 * validity windows do the rest) but never in the past: stopping a lot at an
 * instant it has already been debited at would falsify what happened.
 *
 * `firstPeriodEndsAt` is optional, only meaningful for a subject on no plan,
 * and only ever SHORTENS the first period. Left out, the subject renews on the
 * anniversary of the effective instant, which is what a signing-up user gets.
 * Given, it becomes the renewal anchor: a fleet renewing on the 5th stays on
 * the 5th when one person joins on the 20th, because their first period is a
 * stub ending on the 5th. A subject already on a plan keeps the anchor they
 * have, so the field is refused for them.
 *
 * It cannot push that end further out. A longer first period would grant more
 * than the plan's envelope for a single period, which is a decision no one made
 * — giving extra is a recharge.
 *
 * **No amount.** What the first lot is worth is computed from the plan's
 * `periodCredits`, prorated over what the stub covers. An operator acts
 * on subscriptions and recharges; they do not compose an envelope attached to a
 * plan, so a `base` lot never holds a number the plan did not produce.
 */
export const ChangeSubscriptionRequestSchema = z.object({
    scopeType: ScopeTypeSchema,
    scopeId: z.string().min(1),
    planId: z.string().min(1),
    // `now` or `termEnd`, read on the API's clock, or an instant with an offset.
    effectiveAt: z.union([z.enum(['now', 'termEnd']), z.string()]),
    firstPeriodEndsAt: z.string().optional(),
    // Idempotency. An operator does not replay; a double-clicked form and,
    // later, a payment webhook do.
    clientToken: z.string().optional()
});
/**
 * A user putting themselves on a plan, from now. Only an eligible plan nobody
 * pays for: a paid one waits for payment to exist.
 */
export const ChooseUserPlanRequestSchema = z.object({
    planId: z.string().min(1),
    // Now, or at the end of the commitment in course — what a change that has
    // to wait, or another plan after a cancellation, is taken at.
    at: z.enum(['now', 'termEnd']).optional(),
    // Idempotency: a double-clicked choice lands on the same change.
    clientToken: z.string().optional()
});
/**
 * Put every user who never had a plan on one plan: an operator's action on the
 * whole user base, for a deployment whose users predate its plans. A plan switched
 * off is accepted — this is the console's gesture, not an offer.
 */
export const ApplyPlanBatchRequestSchema = z.object({
    planId: z.string().min(1)
});
/**
 * Add whole months in UTC, clamping the day to the target month's length so an
 * anchor on the 31st does not drift (Jan 31 + 1 month → Feb 28/29).
 *
 * The socle's one piece of calendar arithmetic, defined here once so the
 * proration and the renewal cannot disagree. UTC, deliberately: core-api knows
 * no timezone, so a renewal instant shifts by an hour across a DST change — it
 * oscillates, it does not accumulate.
 */
export function addMonthsUtc(date, months) {
    const shifted = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1, date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()));
    const lastDay = new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0)).getUTCDate();
    shifted.setUTCDate(Math.min(date.getUTCDate(), lastDay));
    return shifted;
}
/**
 * The period that contains `at`, counted from a renewal anchor.
 *
 * Every boundary is the anchor plus a whole number of months, computed from the
 * anchor each time and never from the previous boundary. An anchor on the 31st
 * therefore renews on the 28th in February and on the 31st again in March,
 * where stepping from one boundary to the next would have stayed on the 28th
 * for good.
 *
 * `at` may precede the anchor: the period is then the one ending at the anchor
 * or before it, which is how a first period shortened to align on a fleet's
 * renewal day is measured.
 */
export function periodAt(anchorAt, at) {
    const index = periodIndex(anchorAt, at);
    return { startsAt: boundary(anchorAt, index), endsAt: boundary(anchorAt, index + 1) };
}
/**
 * The commitment that contains `at`: `termPeriods` periods from the start of
 * the commitment in course, then the next ones of the same length, every
 * boundary computed from the anchor.
 *
 * An instant before `termStartsAt` — in a first period shortened to align on a
 * fleet — belongs to the first commitment: that stub does not count towards it.
 */
export function termAt(anchorAt, termStartsAt, termPeriods, at) {
    const first = periodIndex(anchorAt, termStartsAt);
    const term = Math.max(0, Math.floor((periodIndex(anchorAt, at) - first) / termPeriods));
    return {
        startsAt: boundary(anchorAt, first + (term * termPeriods)),
        endsAt: boundary(anchorAt, first + ((term + 1) * termPeriods))
    };
}
/**
 * When the commitment in course at `at` ends: what a cancellation waits for,
 * and a change that may not shorten it.
 *
 * A first period shortened to align on a fleet comes before the first
 * commitment, which is due whole: an instant in it ends with that commitment.
 */
export function commitmentEnd(subscription, at) {
    const { anchorAt, termStartsAt, termPeriods } = subscription;
    return termAt(anchorAt, termStartsAt, termPeriods, at).endsAt;
}
// Which period, counted from the anchor, contains `at`: an estimate from the
// calendar months between the two, corrected by a step either way — the clamp
// to a month's length is what can put it off.
function periodIndex(anchorAt, at) {
    let index = ((at.getUTCFullYear() - anchorAt.getUTCFullYear()) * 12) + (at.getUTCMonth() - anchorAt.getUTCMonth());
    while (boundary(anchorAt, index) > at) {
        index--;
    }
    while (boundary(anchorAt, index + 1) <= at) {
        index++;
    }
    return index;
}
function boundary(anchorAt, index) {
    return addMonthsUtc(anchorAt, index * PERIOD_MONTHS);
}
/**
 * What a period is worth from `from` to its end.
 *
 * The share of the period's actual length that is left — 25 days of a 30-day
 * September, 10 days of a 28-day stretch from February 20 to March 20. That is
 * the measure billing applies to the time spent on a plan, so the credits a
 * period grants and what it is billed cannot disagree.
 *
 * `from` at or before the period's start gives the whole amount; at or after its
 * end, nothing.
 *
 * Whole credits in, whole credits out — this is a plan's amount, not a
 * computed one, and it never crosses into the storage sub-unit here.
 */
export function proratedPeriodCredits(periodCredits, from, period) {
    const length = period.endsAt.getTime() - period.startsAt.getTime();
    const left = period.endsAt.getTime() - Math.max(from.getTime(), period.startsAt.getTime());
    if (left <= 0) {
        return 0;
    }
    return Math.round(periodCredits * (left / length));
}
/** What `planChange` reads of a plan, for the API and the console alike. */
export function planTerms(plan) {
    return {
        periodCredits: plan.limits.periodCredits,
        paid: plan.paid,
        termPeriods: plan.termPeriods,
        autoRenew: plan.autoRenew
    };
}
export function planChange(input) {
    const { effectiveAt, target, current, firstPeriodEndsAt, decidedBy } = input;
    if (!current) {
        return opening({ effectiveAt, target, firstPeriodEndsAt, restart: false });
    }
    if (!current.paid && target.paid) {
        return opening({ effectiveAt, target, firstPeriodEndsAt, restart: true });
    }
    const { anchorAt } = current;
    const period = periodAt(anchorAt, effectiveAt);
    if (firstPeriodEndsAt) {
        return { status: 'renewalDateFixed', renewsAt: period.endsAt };
    }
    // Within a shortened first period the commitment has not started, but it is
    // due whole: what waits for its end waits for the end of the first one.
    const inStub = effectiveAt.getTime() < current.termStartsAt.getTime();
    // The end of a commitment, where the next one starts. The start of the
    // first one is not: nothing has been served yet.
    const atTermBoundary = effectiveAt.getTime() > current.termStartsAt.getTime()
        && termAt(anchorAt, current.termStartsAt, current.termPeriods, effectiveAt).startsAt.getTime() === effectiveAt.getTime();
    const reason = waitReason(current, target);
    const waived = reason && !atTermBoundary ? { until: commitmentEnd(current, effectiveAt), reason } : undefined;
    if (waived && decidedBy !== 'operator') {
        return { status: 'waits', ...waived };
    }
    const termStartsAt = target.termPeriods > current.termPeriods
        ? (inStub ? current.termStartsAt : period.startsAt)
        : (atTermBoundary ? effectiveAt : current.termStartsAt);
    // On a boundary the previous subscription has nothing left of the period:
    // the new plan takes it whole.
    const atBoundary = period.startsAt.getTime() === effectiveAt.getTime();
    const credits = atBoundary
        ? target.periodCredits
        : proratedPeriodCredits(target.periodCredits - current.periodCredits, effectiveAt, period);
    return {
        status: 'granted',
        anchorAt,
        termStartsAt,
        period,
        credits,
        atBoundary,
        restart: false,
        endsAt: target.autoRenew ? undefined : termAt(anchorAt, termStartsAt, target.termPeriods, effectiveAt).endsAt,
        waived
    };
}
function waitReason(current, target) {
    if (current.paid && !target.paid) {
        return 'leavesPaid';
    }
    if (target.periodCredits < current.periodCredits) {
        return 'fewerCredits';
    }
    if (target.termPeriods < current.termPeriods) {
        return 'shorterTerm';
    }
    return undefined;
}
/** A first period: a new anchor, and the commitment starting with it. */
function opening(input) {
    const { effectiveAt, target, firstPeriodEndsAt, restart } = input;
    if (firstPeriodEndsAt && firstPeriodEndsAt.getTime() <= effectiveAt.getTime()) {
        return { status: 'emptyFirstPeriod' };
    }
    const latest = addMonthsUtc(effectiveAt, PERIOD_MONTHS);
    if (firstPeriodEndsAt && firstPeriodEndsAt.getTime() > latest.getTime()) {
        return { status: 'firstPeriodTooLong', latest };
    }
    const anchorAt = firstPeriodEndsAt ?? effectiveAt;
    const period = periodAt(anchorAt, effectiveAt);
    return {
        status: 'granted',
        anchorAt,
        termStartsAt: anchorAt,
        period,
        credits: proratedPeriodCredits(target.periodCredits, effectiveAt, period),
        atBoundary: period.startsAt.getTime() === effectiveAt.getTime(),
        restart,
        endsAt: target.autoRenew ? undefined : termAt(anchorAt, anchorAt, target.termPeriods, effectiveAt).endsAt
    };
}
